import React, { useEffect, useState } from 'react';
import { Tabs, Card, Typography, Tag, Divider, Button, message, Segmented } from 'antd';
import MonacoEditor from '@monaco-editor/react';
import { languageMap } from '../../../../TestSetup/CodingQuestions';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { postData } from '../../../../../../actions/developer';
import { decryptData, encryptData } from '../../../../../../actions/crypto';

const { Title } = Typography;

interface CodingItem {
    _id: string;
    section: { _id: string; name: string };
    codingQuestion: {
        question: string;
        details: string;
        code: string;
        cases: { input: string; output: string; _id: string }[];
        language: string;
    };
    submitedCode: string;
    output?: any
}


const colors = {
    pending: "#FF9500",
    passed: "#009921",
    failed: "#FF0000",
    error: "#9F0000",
}



const CodingTabs = ({ codeData }) => {
    const [codingQuestions, setCodingQuestions] = useState<CodingItem[]>([]);
    const [codeOutput, setCodeOutput] = useState<any>({});
    const [loadingOutput, setLoadingOutput] = useState(false);
    const [outputWindow, setOutPutWindow] = useState<any>({});
    const [size, setSize] = useState(30);

    const dispatch = useDispatch();

    useEffect(() => {
        if (!codeData) return;
        setCodingQuestions(codeData || []);
        setOutput(codeData);
    }, [codeData]);

    // Group questions by section
    const sections = codingQuestions.reduce((acc: any, item) => {
        const sectionName = item.section.name;
        if (!acc[sectionName]) acc[sectionName] = [];
        acc[sectionName].push(item);
        return acc;
    }, {});

    const getColor = (status: any) => {
        switch (true) {
            case status == 3:
                return {
                    color: colors.passed,
                    text: "Success",
                };
            case status == 4:
                return {
                    color: colors.failed,
                    text: "Failed",
                }
            case status > 4:
                return {
                    color: colors.error,
                    text: "Error",
                };
            default:
                return {
                    color: colors.pending,
                    text: "Pending",
                };
        }
    }


    const onRunCode = async (item) => {
        try {
            setLoadingOutput(true);
            setCodeOutput([]);
            setSize(30);
            const submissions = item?.codingQuestion?.cases.map(tc => ({
                language_id: item?.codingQuestion.language,
                source_code: item?.submitedCode,
                stdin: tc.input,
                expected_output: tc.output
            }));

            const response = await dispatch(postData('/code-run', { data: encryptData({ submissions, questionId: item._id, fromAdmin: true }) }));

            if (response.code == 200) {
                setCodeOutput(prev => ({ ...prev, [item._id]: response.data }));
                if (response.data[0]?.compile_output || response.data[0]?.stderr) {
                    setOutPutWindow(prev => ({ ...prev, [item._id]: 'Console' }));
                } else {
                    setOutPutWindow(prev => ({ ...prev, [item._id]: 'Cases' }));
                }
            } else {
                message.error("Error executing code.");
            }
        } catch (error) {
            message.error("Error executing code.");
        } finally {
            setLoadingOutput(false);
        }
    }

    const setOutput = (codeData) => {
        codeData.forEach((item: CodingItem) => {
            setCodeOutput(prev => ({ ...prev, [item._id]: item?.output }));
            setOutPutWindow(prev => ({ ...prev, [item._id]: 'Cases' }));
        });
    }

    return (
        <Tabs type="card">
            {Object.entries(sections).map(([sectionName, questions]: any) => (
                <Tabs.TabPane tab={sectionName} key={sectionName}>
                    {questions.map((item: CodingItem, index: number) => (
                        <Card key={item._id} style={{ marginBottom: 24 }}>
                            <Title level={5}>{index + 1}. {item.codingQuestion.question}</Title>
                            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }} className="ql-container ql-snow !border-none">
                                <div
                                    style={{ flex: 1, minWidth: 300, maxWidth: '50%', overflowY: 'auto', paddingRight: 10 }}
                                    dangerouslySetInnerHTML={{ __html: item.codingQuestion.details }}
                                    className="ql-editor p-4"
                                />

                                <div style={{ flex: 1, minWidth: 300 }}>
                                    <MonacoEditor
                                        height="300px"
                                        theme='vs-dark'
                                        language={languageMap[item.codingQuestion.language] || 'javascript'}
                                        value={item.submitedCode}
                                    />

                                    <div style={{ marginTop: 12 }} >
                                        <div className='flex justify-between items-center'>
                                            <Segmented
                                                options={['Cases', 'Console']}
                                                value={outputWindow[item._id]}
                                                onChange={(value) => {
                                                    setOutPutWindow(prev => ({ ...prev, [item._id]: value }));
                                                }}
                                            />
                                            <Button type='primary' size='small' onClick={() => onRunCode(item)}>
                                                Run Code
                                            </Button>
                                        </div>
                                        <div style={{ marginTop: 12 }} className='bg-[#e5e8f3] py-10'>
                                            <div className="pt-4 p-3 h-full overflow-auto bg-[#e6e9f3]">
                                                {outputWindow[item._id] == 'Cases' && <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-3">
                                                    {item.codingQuestion.cases.map((ele, index) => <div className={`bg-white flex flex-rows justify-between items-center p-2 border border-[${getColor(codeOutput[item._id]?.[index]?.status_id)?.color}] rounded-md px-4`} key={index}>
                                                        <div className="text-gray-800 font-semibold">
                                                            Case {index + 1}
                                                        </div>
                                                        <div className="">
                                                            <div className={`bg-[${getColor(codeOutput[item._id]?.[index]?.status_id)?.color}] w-full h-8 justify-center flex items-center rounded-lg text-white bold px-8`}>
                                                                {loadingOutput && <LoadingOutlined spin={loadingOutput} className="text-white" />}
                                                                {!loadingOutput && getColor(codeOutput[item._id]?.[index]?.status_id)?.text}
                                                            </div>
                                                        </div>
                                                    </div>)}
                                                </div>}
                                                {outputWindow[item._id] == 'Console' && <pre className="text-gray-800 whitespace-pre-wrap">
                                                    {loadingOutput ? <LoadingOutlined spin={loadingOutput} className="text-primary text-2xl" /> :
                                                        codeOutput[item._id]?.[0]?.compile_output || codeOutput[item._id]?.[0]?.stderr || codeOutput[item._id]?.[0]?.stdout || "No Error"}</pre>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </Tabs.TabPane>
            ))}
        </Tabs>
    );
};

export default CodingTabs;
